/** Validation issue exposed by a field binding to UI controls. */
export interface FieldControlIssue {
  readonly message: string
}

/**
 * Framework-neutral contract between editable application state and a UI control.
 *
 * Controls update `value` through `setValue` and call `blur` when the logical
 * editing interaction is left/committed. For a text input that is normally a DOM
 * blur; composite controls can map their own interaction boundary to it.
 *
 * Florisoft field-capable controls follow these integration rules:
 * - `field` owns the value and is mutually exclusive with raw controlled/default
 *   value props.
 * - Empty values are concrete values, not `undefined`: text uses `""`, nullable
 *   scalar/select/date controls use `null`, multi-value controls use `[]`, and
 *   boolean controls use `false`.
 * - Raw change/blur callbacks remain available and are composed with the binding.
 * - `disabled` prevents interaction; `readOnly`, where supported by the control,
 *   remains focusable/inspectable but does not change the value.
 * - UI Field composition owns label/description/error association; controls apply
 *   that metadata to their primary focus/interaction target.
 */
export interface FieldControlBinding<TValue> {
  readonly name: string
  readonly value: TValue
  readonly errors: readonly FieldControlIssue[]
  setValue(value: TValue): void
  blur(): void
}
